/**
 * Focus trap utility that works with Shadow DOM
 * Ensures focus stays within a container element
 */

interface FocusTrapOptions {
  container: HTMLElement | ShadowRoot;
  initialFocus?: HTMLElement;
  restoreFocus?: HTMLElement;
  onEscape?: () => void;
  trapFocus?: boolean;
}

export class FocusTrap {
  private container: HTMLElement | ShadowRoot;
  private initialFocus?: HTMLElement;
  private restoreFocus?: HTMLElement | null;
  private onEscape?: () => void;
  private trapFocus: boolean;
  private previousActiveElement: Element | null = null;
  private isActive = false;
  private keydownHandler: (e: KeyboardEvent) => void;

  constructor(options: FocusTrapOptions) {
    this.container = options.container;
    this.initialFocus = options.initialFocus;
    this.restoreFocus = options.restoreFocus;
    this.onEscape = options.onEscape;
    this.trapFocus = options.trapFocus ?? true;

    this.keydownHandler = this.handleKeydown.bind(this);
  }

  /**
   * Get all focusable elements within the container, including Shadow DOM
   */
  private getFocusableElements(): HTMLElement[] {
    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable="true"]',
    ].join(', ');

    const elements: HTMLElement[] = [];
    const visited = new Set<ShadowRoot | HTMLElement | Document>();

    // Helper function to recursively search for focusable elements
    const findFocusableElements = (root: HTMLElement | ShadowRoot | Document) => {
      if (visited.has(root)) return;
      visited.add(root);

      try {
        // Find focusable elements in current root
        const foundElements = Array.from(root.querySelectorAll(focusableSelectors)) as HTMLElement[];
        elements.push(...foundElements);

        // Search within Shadow DOM - more comprehensive approach
        const allElements = root.querySelectorAll('*');

        allElements.forEach(el => {
          if (el.shadowRoot && !visited.has(el.shadowRoot)) {
            findFocusableElements(el.shadowRoot);
          }
        });

        // Also check if the root itself is a shadow host
        if (root instanceof HTMLElement && root.shadowRoot && !visited.has(root.shadowRoot)) {
          findFocusableElements(root.shadowRoot);
        }
      } catch (error) {
        // Silently handle any access errors for closed shadow roots
        console.debug('Focus trap: Could not access shadow root', error);
      }
    };

    findFocusableElements(this.container);
    visited.clear();

    // Filter out elements that are not visible or not focusable
    const visibleElements = elements.filter(el => {
      try {
        if (!el.isConnected) return false;

        const style = window.getComputedStyle(el);
        const rect = el.getBoundingClientRect();

        return (
          style.display !== 'none' &&
          style.visibility !== 'hidden' &&
          style.opacity !== '0' &&
          !el.hasAttribute('disabled') &&
          !el.hasAttribute('aria-hidden') &&
          rect.width > 0 &&
          rect.height > 0 &&
          (el.offsetParent !== null || style.position === 'fixed' || style.position === 'absolute')
        );
      } catch (error) {
        return false;
      }
    });

    // Ensure all focusable elements have proper tabindex
    visibleElements.forEach(el => {
      if (!el.hasAttribute('tabindex') || el.getAttribute('tabindex') === '-1') {
        el.setAttribute('tabindex', '0');
      }
    });

    // Sort by DOM order for proper tab sequence
    visibleElements.sort((a, b) => {
      const position = a.compareDocumentPosition(b);
      if (position & Node.DOCUMENT_POSITION_FOLLOWING) {
        return -1;
      } else if (position & Node.DOCUMENT_POSITION_PRECEDING) {
        return 1;
      }
      return 0;
    });

    return visibleElements;
  }

  /**
   * Get the actual focused element, accounting for Shadow DOM
   */
  private getActiveElement(): Element | null {
    let activeElement = document.activeElement;

    // Traverse into shadow DOM to find the actual focused element
    while (activeElement && activeElement.shadowRoot && activeElement.shadowRoot.activeElement) {
      activeElement = activeElement.shadowRoot.activeElement;
    }

    return activeElement;
  }

  /**
   * Handle keydown events for focus management
   */
  private handleKeydown(e: KeyboardEvent): void {
    if (!this.isActive) return;

    if (e.key === 'Escape' && this.onEscape) {
      e.preventDefault();
      this.onEscape();
      return;
    }

    if (e.key !== 'Tab') return;

    const focusableElements = this.getFocusableElements();
    const actualActiveElement = this.getActiveElement();

    if (focusableElements.length === 0) return;
    if (focusableElements.length === 1) {
      e.preventDefault();
      focusableElements[0].focus();
      return;
    }

    // Find current element using the actual active element
    let currentIndex = focusableElements.indexOf(actualActiveElement as HTMLElement);

    // If not found by direct reference, try to find by matching attributes
    if (currentIndex === -1 && actualActiveElement) {
      currentIndex = focusableElements.findIndex(el => {
        return (
          el === actualActiveElement ||
          (el.tagName === actualActiveElement.tagName &&
            el.className === actualActiveElement.className &&
            el.id === actualActiveElement.id)
        );
      });
    }

    // Only prevent default and manage focus for wrap-around cases when trapFocus is enabled
    const isLastElement = currentIndex === focusableElements.length - 1;
    const isFirstElement = currentIndex === 0 || currentIndex === -1;
    const needsWrapAround = (!e.shiftKey && isLastElement) || (e.shiftKey && isFirstElement);

    if (needsWrapAround && this.trapFocus) {
      e.preventDefault();
      e.stopPropagation();

      if (!e.shiftKey && isLastElement) {
        // Tab from last element: focus first element
        focusableElements[0].focus();
      } else if (e.shiftKey && isFirstElement) {
        // Shift+Tab from first element: focus last element
        focusableElements[focusableElements.length - 1].focus();
      }
    }
    // For all other cases, let the browser handle natural tab progression
  }

  /**
   * Activate the focus trap
   */
  activate(): void {
    if (this.isActive) return;

    this.previousActiveElement = document.activeElement;
    this.isActive = true;

    // Add event listener to document for global key handling
    document.addEventListener('keydown', this.keydownHandler, true);

    // Set initial focus
    const focusableElements = this.getFocusableElements();
    if (focusableElements.length > 0) {
      const elementToFocus = this.initialFocus || focusableElements[0];
      elementToFocus.focus();
    }
  }

  /**
   * Deactivate the focus trap and restore previous focus
   */
  deactivate(): void {
    if (!this.isActive) return;

    this.isActive = false;
    document.removeEventListener('keydown', this.keydownHandler, true);

    // Restore focus to the element that was focused before the trap was activated
    const elementToRestore = this.restoreFocus || this.previousActiveElement;
    if (elementToRestore && typeof (elementToRestore as HTMLElement).focus === 'function') {
      (elementToRestore as HTMLElement).focus();
    }

    this.previousActiveElement = null;
  }

  /**
   * Check if the focus trap is currently active
   */
  isActivated(): boolean {
    return this.isActive;
  }

  /**
   * Update the container element (useful for dynamic content)
   */
  updateContainer(container: HTMLElement | ShadowRoot): void {
    this.container = container;
  }
}

/**
 * Factory function to create and manage a focus trap
 */
export function createFocusTrap(options: FocusTrapOptions): FocusTrap {
  return new FocusTrap(options);
}
